import React, { useState, useEffect } from 'react';
import { TrendingUp, Users, Calendar, DollarSign, Clock, CheckCircle, XCircle, AlertCircle, BarChart3, PieChart } from 'lucide-react';

interface AnalyticsData {
  totalLeads: number;
  totalBookings: number;
  conversionRate: number;
  averageResponseTime: number;
  revenue: number;
  monthlyGrowth: number;
  leadSources: { source: string; count: number; percentage: number }[];
  bookingStatuses: { status: string; count: number; percentage: number }[];
  businessTypes: { type: string; count: number; percentage: number }[];
  monthlyStats: { month: string; leads: number; bookings: number; revenue: number }[];
  topPerformingDays: { day: string; bookings: number }[];
  recentActivity: { type: string; description: string; timestamp: string }[];
}

const AnalyticsDashboard: React.FC = () => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('30d');

  // Mock data - in real app, this would come from your backend
  useEffect(() => {
    const mockData: AnalyticsData = {
      totalLeads: 156,
      totalBookings: 89,
      conversionRate: 57.1,
      averageResponseTime: 2.3,
      revenue: 26700,
      monthlyGrowth: 23.5,
      leadSources: [
        { source: 'Website', count: 78, percentage: 50.0 },
        { source: 'Referral', count: 45, percentage: 28.8 },
        { source: 'Social Media', count: 21, percentage: 13.5 },
        { source: 'Advertisement', count: 12, percentage: 7.7 }
      ],
      bookingStatuses: [
        { status: 'Completed', count: 45, percentage: 50.6 },
        { status: 'Scheduled', count: 23, percentage: 25.8 },
        { status: 'Confirmed', count: 12, percentage: 13.5 },
        { status: 'Cancelled', count: 7, percentage: 7.9 },
        { status: 'No Show', count: 2, percentage: 2.2 }
      ],
      businessTypes: [
        { type: 'Salon', count: 67, percentage: 42.9 },
        { type: 'Spa', count: 45, percentage: 28.8 },
        { type: 'Barbershop', count: 23, percentage: 14.7 },
        { type: 'Nail Studio', count: 15, percentage: 9.6 },
        { type: 'Tattoo Studio', count: 6, percentage: 3.8 }
      ],
      monthlyStats: [
        { month: 'Jan', leads: 45, bookings: 28, revenue: 8400 },
        { month: 'Feb', leads: 52, bookings: 31, revenue: 9300 },
        { month: 'Mar', leads: 59, bookings: 30, revenue: 9000 }
      ],
      topPerformingDays: [
        { day: 'Monday', bookings: 18 },
        { day: 'Tuesday', bookings: 22 },
        { day: 'Wednesday', bookings: 19 },
        { day: 'Thursday', bookings: 16 },
        { day: 'Friday', bookings: 14 }
      ],
      recentActivity: [
        { type: 'lead', description: 'New lead from Sarah Johnson (Bella Salon)', timestamp: '2 hours ago' },
        { type: 'booking', description: 'Booking confirmed for Mike Chen (Zen Spa)', timestamp: '4 hours ago' },
        { type: 'conversion', description: 'Lisa Rodriguez converted to paid customer', timestamp: '1 day ago' },
        { type: 'lead', description: 'New lead from David Wilson (Wilson\'s Barbershop)', timestamp: '2 days ago' }
      ]
    };

    setTimeout(() => {
      setAnalyticsData(mockData);
      setLoading(false);
    }, 1000);
  }, [selectedPeriod]);

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'lead': return <Users className="h-4 w-4 text-blue-600" />;
      case 'booking': return <Calendar className="h-4 w-4 text-green-600" />;
      case 'conversion': return <DollarSign className="h-4 w-4 text-purple-600" />;
      default: return <AlertCircle className="h-4 w-4 text-gray-600" />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'lead': return 'bg-blue-50 border-blue-200';
      case 'booking': return 'bg-green-50 border-green-200';
      case 'conversion': return 'bg-purple-50 border-purple-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!analyticsData) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Analytics Dashboard</h2>
          <p className="text-gray-600">Track your business performance and growth</p>
        </div>
        <div className="mt-4 sm:mt-0">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Leads</p>
              <p className="text-2xl font-bold text-gray-900">{analyticsData.totalLeads}</p>
              <p className="text-sm text-green-600 flex items-center">
                <TrendingUp className="h-3 w-3 mr-1" />
                +{analyticsData.monthlyGrowth}% this month
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Calendar className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Bookings</p>
              <p className="text-2xl font-bold text-gray-900">{analyticsData.totalBookings}</p>
              <p className="text-sm text-gray-500">
                {analyticsData.conversionRate}% conversion rate
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Revenue</p>
              <p className="text-2xl font-bold text-gray-900">${analyticsData.revenue.toLocaleString()}</p>
              <p className="text-sm text-green-600 flex items-center">
                <TrendingUp className="h-3 w-3 mr-1" />
                +{analyticsData.monthlyGrowth}% this month
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Avg Response Time</p>
              <p className="text-2xl font-bold text-gray-900">{analyticsData.averageResponseTime}h</p>
              <p className="text-sm text-gray-500">to first contact</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Lead Sources */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Lead Sources</h3>
            <PieChart className="h-5 w-5 text-gray-400" />
          </div>
          <div className="space-y-3">
            {analyticsData.leadSources.map((source, index) => (
              <div key={source.source} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-3 ${
                    index === 0 ? 'bg-blue-500' :
                    index === 1 ? 'bg-green-500' :
                    index === 2 ? 'bg-yellow-500' : 'bg-red-500'
                  }`} />
                  <span className="text-sm text-gray-700">{source.source}</span>
                </div>
                <div className="flex items-center">
                  <span className="text-sm font-medium text-gray-900 mr-2">{source.count}</span>
                  <span className="text-xs text-gray-500">({source.percentage}%)</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Booking Statuses */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Booking Statuses</h3>
            <BarChart3 className="h-5 w-5 text-gray-400" />
          </div>
          <div className="space-y-3">
            {analyticsData.bookingStatuses.map((status, index) => (
              <div key={status.status} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-3 ${
                    index === 0 ? 'bg-green-500' :
                    index === 1 ? 'bg-blue-500' :
                    index === 2 ? 'bg-yellow-500' :
                    index === 3 ? 'bg-red-500' : 'bg-gray-500'
                  }`} />
                  <span className="text-sm text-gray-700">{status.status}</span>
                </div>
                <div className="flex items-center">
                  <span className="text-sm font-medium text-gray-900 mr-2">{status.count}</span>
                  <span className="text-xs text-gray-500">({status.percentage}%)</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Business Types and Top Days */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Business Types */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Business Types</h3>
          <div className="space-y-3">
            {analyticsData.businessTypes.map((type, index) => (
              <div key={type.type} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-3 ${
                    index === 0 ? 'bg-blue-500' :
                    index === 1 ? 'bg-green-500' :
                    index === 2 ? 'bg-yellow-500' :
                    index === 3 ? 'bg-purple-500' : 'bg-red-500'
                  }`} />
                  <span className="text-sm text-gray-700 capitalize">{type.type}</span>
                </div>
                <div className="flex items-center">
                  <span className="text-sm font-medium text-gray-900 mr-2">{type.count}</span>
                  <span className="text-xs text-gray-500">({type.percentage}%)</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Performing Days */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Top Performing Days</h3>
          <div className="space-y-3">
            {analyticsData.topPerformingDays.map((day, index) => (
              <div key={day.day} className="flex items-center justify-between">
                <span className="text-sm text-gray-700">{day.day}</span>
                <div className="flex items-center">
                  <div className="w-20 bg-gray-200 rounded-full h-2 mr-3">
                    <div 
                      className="bg-red-500 h-2 rounded-full" 
                      style={{ width: `${(day.bookings / Math.max(...analyticsData.topPerformingDays.map(d => d.bookings))) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-900">{day.bookings}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Recent Activity</h3>
        <div className="space-y-3">
          {analyticsData.recentActivity.map((activity, index) => (
            <div key={index} className={`flex items-center p-3 rounded-lg border ${getActivityColor(activity.type)}`}>
              <div className="flex-shrink-0 mr-3">
                {getActivityIcon(activity.type)}
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-900">{activity.description}</p>
                <p className="text-xs text-gray-500">{activity.timestamp}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Monthly Stats Chart */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Monthly Performance</h3>
        <div className="grid grid-cols-3 gap-4">
          {analyticsData.monthlyStats.map((month) => (
            <div key={month.month} className="text-center">
              <div className="text-2xl font-bold text-gray-900">{month.month}</div>
              <div className="text-sm text-gray-600 mt-1">
                <div>{month.leads} leads</div>
                <div>{month.bookings} bookings</div>
                <div className="text-green-600 font-medium">${month.revenue.toLocaleString()}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;

